import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import find from 'lodash/find';
import invoke from 'lodash/invoke';
import { FormattedMessage, FormattedDate, injectIntl } from 'react-intl';
import ScheduleModal from '../../components/ScheduleModal';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Toast from '../../components/Toast';
import prepareProps from './render-helpers';
import REPVISITS_LOCATION_TYPE from '../../utils/repvisits-location-types';
import { isUrlValid, addProtocol } from '../../utils/url';
import messages from './messages';
import './styles.css';

const ToastKind = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

class VisitRequest extends React.PureComponent {
  static propTypes = {
    node: PropTypes.shape({
      schoolTimezone: PropTypes.string,
      id: PropTypes.string,
      date: PropTypes.string,
      appointments: PropTypes.array,
    }),
    appointmentId: PropTypes.string,
    webInstructions: PropTypes.string,
    defaultVisitUrl: PropTypes.string,
    onClose: PropTypes.func,
    mountNode: PropTypes.any,
    onOpen: PropTypes.func,
    open: PropTypes.bool,
    onConfirm: PropTypes.func,
    intl: PropTypes.any,
    rsvpRepVisit: PropTypes.func,
  };

  state = {
    notifications: [],
    visitUrlError: false,
    visitUrl: '',
    clearLoading: false,
    selectedInPerson: false,
  };

  // eslint-disable-next-line react/no-access-state-in-setstate
  removeNotification = () => { this.state.notifications.shift(); this.setState({ notifications: this.state.notifications }); }

  newNotification = (message, kind, title = '') => {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const localNotifications = this.state.notifications.slice();
    const notification = <Toast active title={title} description={message} onDeactivate={this.removeNotification} kind={kind} />;
    localNotifications.push(notification);
    this.setState({ notifications: localNotifications });
  };

  onVisitUrlChange = (event) => {
    const visitUrl = event.target.value;
    this.setState({ visitUrl, visitUrlError: !visitUrl || !isUrlValid(visitUrl) });
  }

  selectInPerson = () => this.setState({ selectedInPerson: true });

  selectVirtual = () => this.setState({ selectedInPerson: false });

  handleVisitUrl = () => {
    const { node, appointmentId } = this.props;
    const { visitUrl } = this.state;
    const appointmentNode = get(node, 'appointments', []).find((appointment) => (appointment.id === appointmentId));
    const visitLocationType = get(appointmentNode, 'visitLocationType');
    const eitherWithVirtual = visitLocationType === REPVISITS_LOCATION_TYPE.EITHER && !this.state.selectedInPerson;
    if ((!visitUrl || !isUrlValid(visitUrl)) && (visitLocationType === REPVISITS_LOCATION_TYPE.VIRTUAL || eitherWithVirtual)) {
      this.setState({ visitUrlError: true, clearLoading: true }, () => this.setState({ clearLoading: false }));
      return false;
    }
    return true;
  }

  reserve = () => {
    const { intl, node, appointmentId } = this.props;
    const { visitUrl, selectedInPerson } = this.state;
    if (!this.handleVisitUrl()) return;

    invoke(this.props, 'onConfirm');
    const onFailure = () => {
      this.newNotification(intl.formatMessage(messages.error), ToastKind.error);
      this.closeAndResetValues();
    };

    const onSuccess = (response) => {
      const appointment = find(
        get(response, 'data.rsvpRepVisit.repVisit.appointments'),
        { id: this.props.appointmentId },
      );
      const status = get(appointment, 'userBookingStatus');
      if (status === 'APPROVED') {
        this.newNotification(intl.formatMessage(messages.approved),
          ToastKind.success, intl.formatMessage(messages.approvedTitle));
      } else if (status === 'PENDING') {
        this.newNotification(intl.formatMessage(messages.pending),
          ToastKind.success, intl.formatMessage(messages.pendingTitle));
      }
      this.closeAndResetValues();
    };

    const appointmentNode = get(node, 'appointments', []).find((appointment) => (appointment.id === appointmentId));
    let chosenVisitLocationType = get(appointmentNode, 'visitLocationType') || REPVISITS_LOCATION_TYPE.IN_PERSON;

    if (chosenVisitLocationType === REPVISITS_LOCATION_TYPE.EITHER) {
      chosenVisitLocationType = selectedInPerson ? REPVISITS_LOCATION_TYPE.IN_PERSON : REPVISITS_LOCATION_TYPE.VIRTUAL;
    }

    this.props.rsvpRepVisit({
      variables: {
        input: {
          repVisit: {
            dayId: node.id,
            appointmentId,
            visitUrl: addProtocol(visitUrl).slice(0, 2048),
            chosenVisitLocationType,
          },
        },
      },
    }).then(onSuccess, onFailure);
  };

  formatVisitLocationType = (visitLocationType) => {
    if (visitLocationType === REPVISITS_LOCATION_TYPE.VIRTUAL) return ' virtual';
    return ' in-person';
  };

  closeAndResetValues = () => {
    const { onClose } = this.props;
    onClose();
    this.setState({ visitUrl: '' });
  };

  modalIsInValid = (visitLocationType) => {
    if (!visitLocationType) {
      return false;
    }
    const { selectedInPerson, visitUrl } = this.state;
    return REPVISITS_LOCATION_TYPE.IN_PERSON !== visitLocationType && !selectedInPerson && !visitUrl;
  }

  renderHeader = (intl, highSchoolName, visitLocationType, date, timeStr, timezone) => {
    const isVisitInPerson = visitLocationType === REPVISITS_LOCATION_TYPE.IN_PERSON || visitLocationType === null;
    return (
      <div styleName="prompt" data-cy="schedule-prompt">
        {
          visitLocationType === REPVISITS_LOCATION_TYPE.EITHER ?
            intl.formatMessage(messages.promptEither, { name: highSchoolName }) :
            (
              <React.Fragment>
                {intl.formatMessage(messages.prompt1)}
                {isVisitInPerson && 'n'}
                <b>{this.formatVisitLocationType(visitLocationType)}</b>
                {intl.formatMessage(messages.prompt2, { name: highSchoolName })}
              </React.Fragment>
            )
        }
        <b>{`${timeStr} ${timezone}`}</b> on {' '}
        <b>
          <FormattedDate value={`${date}T12:00:00`} weekday="long" year="numeric" month="long" day="numeric" />
        </b>
        ?
      </div>
    );
  }

  renderVisitUrlInput = (intl, visitLocationType, virtual) => {
    if (virtual && visitLocationType !== REPVISITS_LOCATION_TYPE.VIRTUAL) return null;
    if (!virtual && visitLocationType !== REPVISITS_LOCATION_TYPE.EITHER) return null;
    if (!virtual && this.state.selectedInPerson) return null;
    const virtualStyle = virtual ? ' space-top' : '';
    return (
      <div data-cy="visit-url-input-container" styleName={`visit-url-container ${virtualStyle}`}>
        {intl.formatMessage(messages.virtualVisitHint)}
        <Input
          data-cy="visit-url-input"
          styleName="visit-url-input"
          value={this.state.visitUrl}
          error={this.state.visitUrlError}
          onChange={this.onVisitUrlChange}
        />
      </div>
    );
  }

  renderSpecialInstructions = (intl, webInstructions) => {
    if (!webInstructions) return null;
    return (
      <div>
        <p data-cy="special-instructions-hint" styleName="special-instructions-hint">{intl.formatMessage(messages.specialInstructions)}</p>
        <p data-cy="special-instructions" styleName="special-instructions-content">{webInstructions}</p>
      </div>
    );
  }

  renderVisitLocationTypeSelector = (visitLocationType) => {
    if (visitLocationType !== REPVISITS_LOCATION_TYPE.EITHER) return null;
    return (
      <Button.Group fluid>
        <Button
          data-cy="select-in-person-button"
          onClick={this.selectInPerson}
          styleName={!this.state.selectedInPerson ? 'either-location-type' : 'either-location-type-selected'}
        >
          <FormattedMessage {...messages.visitInPerson} />
        </Button>
        <Button
          data-cy="select-virtual-button"
          onClick={this.selectVirtual}
          styleName={this.state.selectedInPerson ? 'either-location-type' : 'either-location-type-selected'}
        >
          <FormattedMessage {...messages.visitVirtual} />
        </Button>
      </Button.Group>
    );
  }

  renderVirtualScheduler = (intl, highSchoolName, visitLocationType, date, timeStr, timezone, webInstructions) => (
    <React.Fragment>
      {this.renderHeader(intl, highSchoolName, visitLocationType, date, timeStr, timezone)}
      {this.renderVisitUrlInput(intl, visitLocationType, true)}
      {this.renderSpecialInstructions(intl, webInstructions)}
      {this.renderVisitLocationTypeSelector(visitLocationType)}
      {this.renderVisitUrlInput(intl, visitLocationType, false)}
    </React.Fragment>
  )

  render() {
    const { node, intl, webInstructions } = this.props;
    const { appointmentNode, highSchool, visitLocationType, timezone, timeStr, modalProps } = prepareProps(this.props, this.state);
    return (
      <div>
        {this.state.notifications}
        {highSchool && node && appointmentNode && (
          <ScheduleModal
            title={intl.formatMessage(messages.title)}
            message={(
              <div styleName="content">
                {this.renderVirtualScheduler(intl, highSchool, visitLocationType, node.date, timeStr, timezone, webInstructions)}
              </div>
            )}
            cancelText={intl.formatMessage(messages.cancel)}
            confirmText={intl.formatMessage(messages.request)}
            onCancel={this.closeAndResetValues}
            closeAndResetValues={this.closeAndResetValues}
            onConfirm={this.reserve}
            {...modalProps}
            isInvalid={this.modalIsInValid(visitLocationType)}
          />
        )}
      </div>
    );
  }
}

export default injectIntl(VisitRequest);
